<template>
  <div data-component-group="package-form" data-component="package-queues">
    <slot name="beforeForm" />

    <div v-if="!isDisabled" class="component-header">
      <magic-container>
        <v-autocomplete
          v-model="priority"
          :items="priorities"
          prepend-inner-icon="import_export"
          label="Priority"
          item-title="name"
          item-value="id"
          :return-object="true"
          required
          :readonly="isDisabled"
          :error-messages="priorityErrors"
          @blur="v$.priority.$touch()"
        />
        <v-autocomplete
          v-model="country"
          :items="countries"
          prepend-inner-icon="public"
          label="Country"
          item-title="name"
          item-value="code"
          :return-object="true"
          required
          :readonly="isDisabled"
          :error-messages="countryErrors"
          @blur="v$.country.$touch()"
        />
      </magic-container>

      <div class="component-actions">
        <v-btn color="secondary" @click="onUpdate">
          <v-icon>{{ triggerIcon }}</v-icon>
          {{ triggerLabel }}
        </v-btn>
      </div>
    </div>
    <div class="component-body">
      <Removables
        v-if="hasItems"
        :items="queues"
        :is-disabled="isDisabled"
        :on-remove="onRemove"
      >
        <template v-slot:item="scope" >
          <div class="item-grid">
            <div class="d-flex align-center">
              <v-icon>public</v-icon>
              <span>{{ scope.item.country.name }}</span>
            </div>
            <AmountLabel label="Priority" size="lg" value="High" />
          </div>
        </template>
      </Removables>
    </div>
  </div>
</template>

<script>
  import { useVuelidate } from '@vuelidate/core'
  import { required } from '@vuelidate/validators';
  import Removables from '../../lists/Removables';
  import AmountLabel from '../../labels/Amount';
  import { isObj, isStr, isNum } from '../../../utils';

  // TODO: Translate component

  export default {
    components: {
      Removables,
      AmountLabel,
    },
    setup () {
    return {
      v$: useVuelidate()
    }
  },
    validations() {
      return {
        priority: {
          required,
        },
        country: {
          required,
        },
      };
    },
    props: {
      packageItem: {
        type: Object,
        default: undefined,
      },
      queues: {
        type: Array,
        default() {
          return [];
        },
      },
      triggerIcon: {
        type: String,
        default: 'edit',
      },
      triggerLabel: {
        type: String,
        default: 'Attach',
      },
      isDisabled: {
        type: Boolean,
        default: false,
      },
      onSubmit: {
        type: Function,
        default: () => {},
      },
      onRemove: {
        type: Function,
        default: () => {},
      },
    },
    data() {
      return {
        priority: null,
        country: null,
      };
    },
    computed: {
      priorities() {
        return this.$store.getters['packages/queueTypes'];
      },
      countries() {
        return this.$store.getters['users/countries'];
      },
      hasItems() {
        return this.queues.length > 0;
      },
      priorityErrors() {
        return this.v$.priority.$errors.map(error => {
          if (error.$validator === 'required') {
            return 'Priority is required';
          }
          return error.$message;
        });
      },
      countryErrors() {
        return this.v$.country.$errors.map(error => {
          if (error.$validator === 'required') {
            return 'Country is required';
          }
          return error.$message;
        });
      },
    },
    watch: {
      packageItem(nextPackageItem) {
        if (isObj(nextPackageItem)) {
          this.clear();
        }
      },
    },
    created() {
      if (this.priorities.length === 0) {
        this.getQueueTypes();
      }
    },
    methods: {
      getQueueTypes() {
        return this.$store.dispatch('packages/getQueueTypes');
      },
      onUpdate() {
        this.v$.$touch();

        if (!this.canSubmitRequest() || this.v$.$error || this.v$.pending) {
          return false;
        }

        const { priority, country, onSubmit } = this;

        onSubmit({
          queue: priority.id,
          country,
        });

        this.clear();
      },
      canSubmitRequest() {
        const { priority, country } = this;

        return isNum(priority.id) && isStr(country.code);
      },
      clear() {
        this.v$.$reset();
        this.country = null;
      },
    },
  };
</script>

<style lang="scss">
@use 'sass:map';
@use '../../../assets/styles/theme' as *;

[data-component-group='package-form'][data-component='package-queues'] {
  padding: $spacing-xl;
  background-color: rgb(var(--v-theme-surface));
  border-top: 1px solid map.get($colors, grey, lighten-2);

  .component-actions {
    padding: 8px 0 0;
    text-align: right;

    .v-btn {
      margin: 0;

      .v-icon {
        margin: 0 6px 0 0;
      }
    }
  }

  .labels {
    display: flex;
    justify-content: flex-start;
    align-items: center;
    flex-wrap: wrap;
  }

  .label {
    display: flex;
    justify-content: flex-start;
    align-items: center;
    font-size: $text-size-sm;
    padding: 2px 6px;
    margin: 0 10px 10px 0;
    border-radius: $border-radius;
    color: map.get($colors, shades, 'white');
    background-color: map.get($colors, primary);

    .v-icon {
      font-size: $text-size-md;
      margin: 0 6px 0 0;
      color: map.get($colors, shades, 'white');
    }
  }

  [data-component='amount'] {
    display: flex;
    justify-content: flex-start;
    align-items: center;
  }

  [data-component='removables'] {
    padding: 16px 0 0 0;

    .v-list {
      padding: 0;
    }

    .v-list-item {
      height: 62px;
      border-radius: $border-radius;
      background-color: rgba(var(--v-theme-on-surface), 0.06);
    }
  }
}
</style>
